import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Select from '@shared/components/Select/Select';
import { useTranslation } from 'react-i18next';

import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { getDefaultRoleTranslation } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import getAssignableWorkspaceRoles from 'utils/permissions/getAssignableWorkspaceRoles';

interface RoleDropdownProps {
  roleId?: string;
  roleName: string;
  onRoleChange: (roleId: string) => void;
}

// Manage-roles gate (MIP-035): an actor can assign roles whose workspace PL is strictly
// below their own. Owner is never assignable. AC-292.
const RoleDropdown = ({ roleId, roleName, onRoleChange }: RoleDropdownProps) => {
  const { t } = useTranslation();
  const { workspaceId, workspaceRoles } = useWorkspacesContext({ requireWorkspaceId: true });
  const actorPowerLevels = useScopedUserPowerLevels(workspaceId);

  const availableRoles = getAssignableWorkspaceRoles(workspaceRoles, actorPowerLevels.workspace);
  const options: DropdownSelectionProps[] = availableRoles.map(({ id, name, defaultRoleType }) => ({
    title: getDefaultRoleTranslation(defaultRoleType, name) || name,
    onClick: () => onRoleChange(id),
    isSelected: roleId === id,
  }));

  // AC-267: empty filtered list → render as disabled with placeholder label.
  const isEmpty = options.length === 0;
  const displayLabel = isEmpty ? t('workspaces.no_assignable_roles') : roleName;

  return (
    <Select
      size="sm"
      options={options}
      className="md:min-w-[180px]"
      wrapperClassName="w-full md:w-auto"
      disabled={isEmpty}
    >
      {displayLabel}
    </Select>
  );
};

export default RoleDropdown;
